import { uniqueId } from "library/database/split-id.mjs"
import { generate } from "library/guid/index.mjs"
import { isIdInGroup } from "library/id-helpers.mjs"
import { parentTreeItem } from "library/tree/index.mjs"

import { registerSymbol } from "library/serialize-circular.mjs"
import { getScheduleRecordId, isGlobalTailoringId } from "library/get-unique-id-from-tree.mjs"
import { PlanRoot } from "library/tree/constants.mjs"

export const idIndex = registerSymbol(Symbol("idIndex"))
export const DontIndexChildren = registerSymbol(Symbol("dontIndexChildren"))

export function getTreeItemIndex(root, index = {}) {
    if (!root) return {}
    Object.assign(index, { [root.id]: root, [idIndex]: {}, retrieve, addToIndex })
    recurseAdd(root, index)
    return index

    function addToIndex(parent, itemOrList) {
        if (Array.isArray(itemOrList)) {
            addChildren(parent, itemOrList, index)
        } else {
            recurseAdd(parent, index)
        }
    }

    function retrieve(item, hint) {
        if (typeof item === "string" && isIdInGroup(item)) {
            return retrieve(item.split("!").at(-1), item)
        }
        hint ??= generate()
        return index[hint] ?? index[idIndex][item]?.[0] ?? index[item] ?? index[idIndex][`${item}:sfg20/schedule`]?.[0]
    }
}

function addChildren(parent, children, index, seen = new Set()) {
    for (const item of (children ?? []).filter(Boolean)) {
        if (!(parentTreeItem in item)) {
            item[parentTreeItem] = parent
            if (item._id && !isGlobalTailoringId(item.id)) {
                item.id = `${parent.id}!${uniqueId(item._id)}`
            }
        }
        index[item.id] = item
        indexTreeQualifiedScheduleRoutes(item, parent, index)

        if (!item.isAlias) {
            if (item._id) {
                const list = (index[idIndex][item._id] = index[idIndex][item._id] || [])
                list.push(item)
            }
            index[getScheduleRecordId(item.id)] = item // we may need to remove getUniqueId
        }
        if (!item[DontIndexChildren]) {
            if (item.children) {
                recurseAdd(item, index, seen)
            }
        }
    }
}

function indexTreeQualifiedScheduleRoutes(item, parent, index) {
    if (!isScheduleRecordItem(item) || !item.id || item.id.includes("~") || !item.id.includes("!")) return
    const tree = findPlanRoot(parent)
    if (!tree?.id) return

    const scheduleId = item.id.split("!").at(-1)
    let group = parent
    while (group && !group[PlanRoot]) {
        if (group.id) {
            index[`${getUnqualifiedGroupId(group.id)}~${tree.id}!${scheduleId}`] = item
        }
        group = group[parentTreeItem]
    }
}

function isScheduleRecordItem(item) {
    return `${item?._id ?? ""}`.includes("/schedule")
}

function getUnqualifiedGroupId(id) {
    return `${id}`.split("!").at(-1).split("~").at(0)
}

function findPlanRoot(item) {
    let scan = item
    while (scan && !scan[PlanRoot]) {
        scan = scan[parentTreeItem]
    }
    return scan
}

function recurseAdd(root, index, seen = new Set()) {
    if (seen.has(root)) return
    seen.add(root)
    addChildren(root, root.children, index, seen)
}
